import { useEffect, useRef } from 'react';

/**
 * The homepage hero's "live" background (2026-10-10): faint circuit-board
 * traces; small pulses of light run along them, and near the mouse the traces
 * light up and the pulses head toward the pointer.
 *
 * Cost control: nothing runs until the page has loaded and the browser is idle
 * (the hero text is the LCP and must not wait); the loop pauses when the hero
 * is off screen or the tab is hidden; reduced motion → the traces are drawn
 * once, no animation; never during prerendering (the canvas stays empty).
 */
interface Trace { pts: [number, number][]; len: number; segs: number[] }
interface Pulse { t: Trace; d: number; dir: 1 | -1; speed: number; life: number }

const CELL = 44;
const prerendering = () => !!(window as unknown as { __BPC_PRERENDER__?: boolean }).__BPC_PRERENDER__;

function buildTraces(w: number, h: number): Trace[] {
  const traces: Trace[] = [];
  const count = Math.round((w * h) / 26000);
  const dirs: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];
  for (let i = 0; i < count; i++) {
    let x = Math.round((Math.random() * w) / CELL) * CELL;
    let y = Math.round((Math.random() * h) / CELL) * CELL;
    const pts: [number, number][] = [[x, y]];
    let [dx, dy] = dirs[Math.floor(Math.random() * 4)];
    const steps = 2 + Math.floor(Math.random() * 5);
    for (let s = 0; s < steps; s++) {
      const run = (1 + Math.floor(Math.random() * 4)) * CELL;
      x += dx * run;
      y += dy * run;
      pts.push([x, y]);
      // PCB style: mostly straight, sometimes a 45° bend, rarely 90°.
      const r = Math.random();
      if (r < 0.45) {
        const turn = dirs.filter(([a, b]) => (a !== dx || b !== dy) && (a !== -dx || b !== -dy) && Math.abs(a * dx + b * dy) > 0);
        if (turn.length) [dx, dy] = turn[Math.floor(Math.random() * turn.length)];
      } else if (r < 0.55) {
        [dx, dy] = dx === 0 || dy === 0 ? [dy, dx] : [dx, 0];
      }
    }
    const segs: number[] = [];
    let len = 0;
    for (let k = 1; k < pts.length; k++) {
      const l = Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
      segs.push(l);
      len += l;
    }
    traces.push({ pts, len, segs });
  }
  return traces;
}

function pointAt(t: Trace, d: number): [number, number] {
  let rest = Math.min(Math.max(d, 0), t.len);
  for (let k = 0; k < t.segs.length; k++) {
    if (rest <= t.segs[k]) {
      const f = t.segs[k] ? rest / t.segs[k] : 0;
      const [x0, y0] = t.pts[k];
      const [x1, y1] = t.pts[k + 1];
      return [x0 + (x1 - x0) * f, y0 + (y1 - y0) * f];
    }
    rest -= t.segs[k];
  }
  return t.pts[t.pts.length - 1];
}

function drawLayer(c: HTMLCanvasElement, traces: Trace[], dpr: number, stroke: string, pad: string) {
  const g = c.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, c.width, c.height);
  g.lineWidth = 1.4;
  g.lineJoin = 'round';
  g.strokeStyle = stroke;
  g.fillStyle = pad;
  for (const t of traces) {
    g.beginPath();
    g.moveTo(t.pts[0][0], t.pts[0][1]);
    for (let k = 1; k < t.pts.length; k++) g.lineTo(t.pts[k][0], t.pts[k][1]);
    g.stroke();
    for (const [x, y] of [t.pts[0], t.pts[t.pts.length - 1]]) {
      g.beginPath();
      g.arc(x, y, 3, 0, Math.PI * 2);
      g.fill();
    }
  }
}

export default function CircuitField() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || prerendering()) return;
    const host = canvas.parentElement!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    let started = false;
    let disposed = false;
    let raf = 0;
    let visible = true;
    let w = 0;
    let h = 0;
    let dpr = 1;
    let traces: Trace[] = [];
    const base = document.createElement('canvas');
    const bright = document.createElement('canvas');
    const glow = document.createElement('canvas');
    const pulses: Pulse[] = [];
    let pointer: [number, number] | null = null;
    // Touch screens have no hover (2026-10-14): a light wanders over the board
    // on its own, and a tap pulls it to the finger for a moment.
    let tapPoint: [number, number] | null = null;
    let tapUntil = 0;
    let lastSpawn = 0;
    let last = 0;

    const ctx = canvas.getContext('2d')!;

    const layout = () => {
      const r = host.getBoundingClientRect();
      w = Math.max(1, Math.round(r.width));
      h = Math.max(1, Math.round(r.height));
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      for (const c of [canvas, base, bright, glow]) {
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
      }
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      traces = buildTraces(w, h);
      pulses.length = 0;
      drawLayer(base, traces, dpr, 'rgba(143,179,255,0.10)', 'rgba(143,179,255,0.16)');
      drawLayer(bright, traces, dpr, 'rgba(143,179,255,0.55)', 'rgba(185,187,255,0.75)');
    };

    const near = (t: Trace, p: [number, number], r: number) =>
      t.pts.some(([x, y]) => Math.abs(x - p[0]) < r && Math.abs(y - p[1]) < r);

    const spawn = (now: number) => {
      const max = fine ? 14 : 10;
      const gap = pointer ? (fine ? 90 : 150) : 420;
      if (pulses.length >= max || now - lastSpawn < gap || !traces.length) return;
      lastSpawn = now;
      let pool = traces;
      if (pointer) {
        const close = traces.filter((t) => near(t, pointer!, 200));
        if (close.length) pool = close;
      }
      const t = pool[Math.floor(Math.random() * pool.length)];
      let dir: 1 | -1 = Math.random() < 0.5 ? 1 : -1;
      if (pointer) {
        // Run toward the end nearer the pointer.
        const a = t.pts[0];
        const b = t.pts[t.pts.length - 1];
        dir = Math.hypot(a[0] - pointer[0], a[1] - pointer[1]) > Math.hypot(b[0] - pointer[0], b[1] - pointer[1]) ? 1 : -1;
      }
      pulses.push({ t, d: dir === 1 ? 0 : t.len, dir, speed: 140 + Math.random() * 160, life: 1 });
    };

    const frame = (now: number) => {
      raf = 0;
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(base, 0, 0);
      if (!fine) {
        const s = now / 1000;
        pointer = now < tapUntil && tapPoint
          ? tapPoint
          : [w * (0.5 + 0.36 * Math.sin(s * 0.21)), h * (0.42 + 0.28 * Math.sin(s * 0.29 + 1.3))];
      }
      if (pointer) {
        const g = glow.getContext('2d')!;
        g.setTransform(1, 0, 0, 1, 0, 0);
        g.globalCompositeOperation = 'source-over';
        g.clearRect(0, 0, glow.width, glow.height);
        g.drawImage(bright, 0, 0);
        g.globalCompositeOperation = 'destination-in';
        const [px, py] = [pointer[0] * dpr, pointer[1] * dpr];
        const rad = g.createRadialGradient(px, py, 0, px, py, (fine ? 230 : 170) * dpr);
        rad.addColorStop(0, 'rgba(0,0,0,1)');
        rad.addColorStop(1, 'rgba(0,0,0,0)');
        g.fillStyle = rad;
        g.fillRect(0, 0, glow.width, glow.height);
        ctx.drawImage(glow, 0, 0);
      }
      spawn(now);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'lighter';
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.d += p.dir * p.speed * dt;
        const done = p.d < 0 || p.d > p.t.len;
        if (done) p.life -= dt * 3;
        if (p.life <= 0) {
          pulses.splice(i, 1);
          continue;
        }
        // A short tail behind the head.
        for (let k = 6; k >= 0; k--) {
          const [x, y] = pointAt(p.t, p.d - p.dir * k * 7);
          const a = (1 - k / 7) * p.life;
          ctx.fillStyle = `rgba(150,190,255,${0.5 * a})`;
          ctx.beginPath();
          ctx.arc(x, y, k === 0 ? 2.6 : 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
        const [hx, hy] = pointAt(p.t, p.d);
        const halo = ctx.createRadialGradient(hx, hy, 0, hx, hy, 14);
        halo.addColorStop(0, `rgba(170,200,255,${0.55 * p.life})`);
        halo.addColorStop(1, 'rgba(170,200,255,0)');
        ctx.fillStyle = halo;
        ctx.fillRect(hx - 14, hy - 14, 28, 28);
      }
      ctx.globalCompositeOperation = 'source-over';
      if (visible && !document.hidden) raf = requestAnimationFrame(frame);
    };

    const kick = () => {
      if (started && !raf && visible && !document.hidden && !reduced) {
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    };

    const start = () => {
      if (disposed || started) return;
      started = true;
      layout();
      canvas.style.opacity = '1';
      if (reduced) ctx.drawImage(base, 0, 0);
      else kick();
    };

    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      pointer = [e.clientX - r.left, e.clientY - r.top];
    };
    const onLeave = () => (pointer = null);
    const onTap = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      const r = host.getBoundingClientRect();
      tapPoint = [e.clientX - r.left, e.clientY - r.top];
      tapUntil = performance.now() + 1800;
      lastSpawn = 0;
    };
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      kick();
    });
    const ro = new ResizeObserver(() => {
      if (!started) return;
      layout();
      if (reduced) ctx.drawImage(base, 0, 0);
    });
    const onVis = () => kick();

    const idle = () =>
      'requestIdleCallback' in window ? window.requestIdleCallback(start, { timeout: 3000 }) : setTimeout(start, 1500);
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
    io.observe(host);
    ro.observe(host);
    if (fine) {
      host.addEventListener('pointermove', onMove);
      host.addEventListener('pointerleave', onLeave);
    } else {
      host.addEventListener('pointerdown', onTap, { passive: true });
    }
    document.addEventListener('visibilitychange', onVis);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerleave', onLeave);
      host.removeEventListener('pointerdown', onTap);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 opacity-0 transition-opacity duration-1000"
    />
  );
}
